/**
 * IntegratedLayoutManagement - 통합 레이아웃 관리
 * DDD 리팩토링: 2,905 lines → 목표 <1,500 lines
 *
 * 모든 상태 관리, API 호출, 비즈니스 로직을 DDD 계층으로 이동
 * 이 컴포넌트는 UI 렌더링만 담당
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  Cog6ToothIcon,
  DeviceTabletIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  PlusIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Bars3Icon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { DndProvider, useDrag, useDrop, useDragLayer } from 'react-dnd';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';

// DDD Layers
import { useLayoutData } from '../use-cases';
import { layoutService } from '../services';
import { layoutRepository } from '../repositories';
import { renderArchitectureElement } from '../utils/layoutRenderers';
// 공유 컴포넌트에서 상수와 SeatItem 가져오기
import { SeatItem, CELL_SIZE, SNAP_SIZE, ZONE_COLORS, isLightColor, CanvasSeatData } from './shared';
import { ZoneSeatsModal } from './shared/ZoneSeatPicker';
import type {
  Room,
  Seat,
  Zone,
  Device,
  ArchitectureElement,
  ZoneCreateRequest,
  DraggableItem,
  SeatAttributes,
  GridPosition,
} from '../models';

const IntegratedLayoutManagement: React.FC = () => {
  // ==================== DDD Hook ====================
  const {
    // Data
    selectedRoom,
    rooms,
    seats,
    zones,
    devices,
    architectureElements,
    loading,
    error,

    // UI State
    selectedItem,
    viewMode,
    scale,

    // Setters
    setSelectedRoom,
    setSelectedItem,
    setViewMode,
    setScale,
    saving,

    // Actions
    loadAllData,
    loadRoomData,
    createZone,
    updateZone,
    deleteZone,
    deleteSeat,
    createDevice,
    updateDevice,
    deleteDevice,
    createArchitecture,
    updateArchitecture,
    deleteArchitecture,
    handleDrop,
    saveLayout,
  } = useLayoutData();

  // ==================== Auto Scale ====================
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // 현재 선택된 룸 정보 (자동 스케일에 사용)
  const currentRoom = rooms.find(r => r.id === selectedRoom);

  // 화면에 맞추기 (자동 스케일 계산)
  const fitToScreen = useCallback(() => {
    const room = rooms.find(r => r.id === selectedRoom);
    if (!room || !gridContainerRef.current) return;

    const container = gridContainerRef.current;
    const containerWidth = container.clientWidth - 48; // padding 제외
    const containerHeight = container.clientHeight - 48;

    const gridWidth = room.grid_width * CELL_SIZE;
    const gridHeight = room.grid_height * CELL_SIZE;

    const scaleX = containerWidth / gridWidth;
    const scaleY = containerHeight / gridHeight;

    // 더 작은 비율로 맞추되 최소 0.3, 최대 1.5
    const newScale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.3), 1.5);
    setScale(newScale);
  }, [rooms, selectedRoom, setScale]);

  // 룸 변경 시 자동으로 화면에 맞추기
  useEffect(() => {
    if (selectedRoom && rooms.length > 0) {
      // 약간의 딜레이 후 실행 (DOM 렌더링 완료 대기)
      const timer = setTimeout(() => {
        fitToScreen();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedRoom, rooms, fitToScreen]);

  // ==================== Local UI State ====================
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showZoneSidebar, setShowZoneSidebar] = useState(true);

  // 드래그 중인 좌석의 실시간 위치 (좌석 자체가 움직이도록)
  const [draggingSeat, setDraggingSeat] = useState<{
    seatNumber: number;
    x: number;
    y: number;
    isColliding: boolean;
    errorMessage: string;
  } | null>(null);

  // 드래그 시작 정보 (동기적 접근용 - 클릭과 드래그 구분)
  const dragStartRef = useRef<{
    seatNumber: number;
    startX: number;
    startY: number;
    originalX: number;
    originalY: number;
    offsetX: number;  // 좌석 내 클릭 위치 오프셋
    offsetY: number;
    hasMoved: boolean;
  } | null>(null);

  // 최소 이동 거리 (이 이하면 클릭으로 간주)
  const MIN_DRAG_DISTANCE = 5;

  // Zone Modal
  const [showZoneModal, setShowZoneModal] = useState(false);
  // 2026-10-04 구역 좌석 지정 창 / 구역 삭제 두 번 누르기(window.confirm 금지 규칙)
  const [seatZone, setSeatZone] = useState<Zone | null>(null);
  const [confirmDeleteZoneId, setConfirmDeleteZoneId] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [zoneForm, setZoneForm] = useState<ZoneCreateRequest>({
    room_id: '',
    name: '',
    description: '',
    color: '#3B82F6',
    premium_rate: 1.0,
  });

  // Device Modal
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [newDevice, setNewDevice] = useState<Partial<Device>>({
    device_type: 'face_recognition_gate',
    device_name: '',
    position_x: 0,
    position_y: 0,
    is_active: true,
    network_info: {},
    status: 'offline',
  });

  // Architecture Modal
  const [showArchitectureModal, setShowArchitectureModal] = useState(false);
  const [newArchitecture, setNewArchitecture] = useState<Partial<ArchitectureElement>>({
    element_type: 'pillar',
    position_x: 0,
    position_y: 0,
    width: 80,
    height: 80,
    rotation: 0,
    wall_position: 'top',
  });

  // Grid Size Modal
  const [showGridSizeModal, setShowGridSizeModal] = useState(false);
  const [gridSizeForm, setGridSizeForm] = useState({
    width: 0,
    height: 0,
  });
  // 그리드 크기 변경용 미터 입력
  const [gridSizeRealForm, setGridSizeRealForm] = useState({
    width_meters: 10,
    height_meters: 8,
  });

  // Seat Attributes Modal
  const [showSeatAttributesModal, setShowSeatAttributesModal] = useState(false);
  const [selectedSeatForEdit, setSelectedSeatForEdit] = useState<Seat | null>(null);
  const [seatAttributesForm, setSeatAttributesForm] = useState<{
    seat_type: 'standard' | 'premium' | 'deluxe';
    attributes: SeatAttributes;
  }>({
    seat_type: 'standard',
    attributes: {
      has_power_outlet: true,
      has_desk_lamp: true,
      is_window_seat: false,
      partition_height: 'medium',
      monitor_compatible: false,
      has_storage: false,
    },
  });

  // Room Modal
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showRoomOrderModal, setShowRoomOrderModal] = useState(false);
  const [showRoomEditModal, setShowRoomEditModal] = useState(false);
  const [showRoomDeleteConfirm, setShowRoomDeleteConfirm] = useState(false);
  const [roomEditForm, setRoomEditForm] = useState({ name: '', description: '' });
  const [roomForm, setRoomForm] = useState({
    name: '',
    description: '',
    grid_width: 20,
    grid_height: 15,
  });
  // 실제 크기 입력용 (미터 단위)
  const [useRealSize, setUseRealSize] = useState(true);
  const [realSizeForm, setRealSizeForm] = useState({
    width_meters: 10,  // 가로 10m
    height_meters: 8,  // 세로 8m
  });
  const METERS_PER_CELL = 0.8; // 1셀 = 0.8m (실제 독서실 책상 크기 기준)

  // Auto Layout Modal
  const [showAutoLayoutModal, setShowAutoLayoutModal] = useState(false);
  const [autoLayoutColumns, setAutoLayoutColumns] = useState(6);
  const [autoLayoutDirection, setAutoLayoutDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [autoLayoutSaving, setAutoLayoutSaving] = useState(false);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState<'device' | 'architecture' | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);

  // Add Seat Mode
  const [addSeatMode, setAddSeatMode] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState<GridPosition[]>([]);
  const [lastSelectedPosition, setLastSelectedPosition] = useState<GridPosition | null>(null);
  const [showAddSeatModal, setShowAddSeatModal] = useState(false);
  const [newSeatForm, setNewSeatForm] = useState({
    zone_id: '',
    seat_type: 'standard' as 'standard' | 'premium' | 'deluxe',
    direction: 'north' as 'north' | 'south' | 'east' | 'west',
    start_number: 1,
  });

  // ==================== Room 순서 변경 ====================
  const moveRoom = async (index: number, direction: 'up' | 'down') => {
    const newRooms = [...rooms];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newRooms.length) return;

    [newRooms[index], newRooms[targetIndex]] = [newRooms[targetIndex], newRooms[index]];
    // rooms 상태 직접 변경 (useLayoutData에서 setRooms 노출 필요 - 아래서 loadAllData로 대체)
    await layoutRepository.reorderRooms(newRooms.map(r => r.id));
    await loadAllData();
  };

  // ==================== Effects ====================
  useEffect(() => {
    loadAllData();
  }, []);

  // ==================== 페이지 이탈 감지 ====================
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // ==================== Drop Handler with Change Tracking ====================
  const handleDropWithTracking = (item: DraggableItem, position: GridPosition) => {
    handleDrop(item, position);
    setHasUnsavedChanges(true);
  };

  // ==================== Save Handler with Change Reset ====================
  const handleSaveLayout = async () => {
    console.log('🔵 handleSaveLayout 호출됨');
    console.log('📊 hasUnsavedChanges:', hasUnsavedChanges);
    console.log('📊 selectedRoom:', selectedRoom);
    console.log('📊 현재 seats 상태:', seats.map(s => ({ num: s.seat_number, x: s.position_x, y: s.position_y, room_id: s.room_id })));

    try {
      const success = await saveLayout();
      console.log('💾 saveLayout 결과:', success);
      if (success) {
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('💥 saveLayout 에러:', error);
    }
  };

  // ==================== Zone Handlers ====================
  const handleZoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // room_id 검증
    if (!selectedRoom) {
      alert('룸을 먼저 선택해주세요.');
      return;
    }

    let success: boolean;

    if (editingZone) {
      success = await updateZone(editingZone.id, zoneForm);
    } else {
      // room_id를 포함하여 구역 생성
      success = await createZone({
        ...zoneForm,
        room_id: selectedRoom,
      });
    }

    if (success) {
      setShowZoneModal(false);
      setEditingZone(null);
      setZoneForm({ room_id: '', name: '', description: '', color: '#3B82F6', premium_rate: 1.0 });
    }
  };

  const openZoneModal = (zone?: Zone) => {
    if (zone) {
      setEditingZone(zone);
      setZoneForm({
        room_id: zone.room_id || '',
        name: zone.name,
        description: zone.description || '',
        color: zone.color || '#3B82F6',
        premium_rate: zone.premium_rate,
      });
    } else {
      setEditingZone(null);
      setZoneForm({ room_id: '', name: '', description: '', color: '#3B82F6', premium_rate: 1.0 });
    }
    setShowZoneModal(true);
  };

  // ==================== Device Handlers ====================
  const handleAddDevice = async () => {
    const success = await createDevice(newDevice);
    if (success) {
      setShowDeviceModal(false);
      setNewDevice({
        device_type: 'face_recognition_gate',
        device_name: '',
        position_x: 0,
        position_y: 0,
        is_active: true,
        network_info: {},
        status: 'offline',
      });
    }
  };

  const handleEditDevice = (device: Device) => {
    setEditingItem({ ...device });
    setEditType('device');
    setShowEditModal(true);
  };

  const handleSaveDeviceEdit = async () => {
    if (!editingItem) return;
    const success = await updateDevice(editingItem.id, editingItem);
    if (success) {
      setShowEditModal(false);
      setEditingItem(null);
      setEditType(null);
    }
  };

  // ==================== Grid Size Handlers ====================
  const openGridSizeModal = () => {
    if (!currentRoom) {
      alert('룸을 먼저 선택해주세요.');
      return;
    }
    setGridSizeForm({
      width: currentRoom.grid_width,
      height: currentRoom.grid_height,
    });
    // 현재 크기를 미터로 역계산
    setGridSizeRealForm({
      width_meters: currentRoom.grid_width * METERS_PER_CELL,
      height_meters: currentRoom.grid_height * METERS_PER_CELL,
    });
    setShowGridSizeModal(true);
  };

  const handleUpdateGridSize = async () => {
    if (!selectedRoom) return;

    if (gridSizeForm.width < 5 || gridSizeForm.width > 50) {
      alert('가로 크기는 5~50 사이로 설정해주세요.');
      return;
    }
    if (gridSizeForm.height < 5 || gridSizeForm.height > 50) {
      alert('세로 크기는 5~50 사이로 설정해주세요.');
      return;
    }

    try {
      await layoutRepository.updateRoomGridSize(selectedRoom, gridSizeForm.width, gridSizeForm.height);
      alert('✅ 그리드 크기가 변경되었습니다!');
      setShowGridSizeModal(false);
      // rooms 목록도 다시 불러와야 grid_width, grid_height가 업데이트됨
      await loadAllData();
    } catch (error) {
      console.error('그리드 크기 변경 실패:', error);
      alert('❌ 그리드 크기 변경에 실패했습니다: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    }
  };

  // ==================== Auto Layout Handler ====================
  const handleAutoLayout = async () => {
    if (!selectedRoom || seats.length === 0) return;

    const totalSeats = seats.length;
    const columns = autoLayoutColumns;
    const rows = Math.ceil(totalSeats / columns);

    // 좌석 크기 및 간격 상수
    const SEAT_WIDTH = 80;
    const SEAT_HEIGHT = 60;
    const GAP_X = 20;
    const GAP_Y = 20;
    const PADDING = 40;

    // 좌석 번호 순으로 정렬
    const sortedSeats = [...seats].sort((a, b) =>
      String(a.seat_number).localeCompare(String(b.seat_number), undefined, { numeric: true })
    );

    setAutoLayoutSaving(true);

    try {
      // 각 좌석의 새 위치 계산 및 업데이트
      const updatePromises = sortedSeats.map(async (seat, index) => {
        let col: number, row: number;
        if (autoLayoutDirection === 'vertical') {
          // 세로 배치: 위→아래, 좌→우
          col = Math.floor(index / rows);
          row = index % rows;
        } else {
          // 가로 배치: 좌→우, 위→아래
          col = index % columns;
          row = Math.floor(index / columns);
        }
        const newX = PADDING + col * (SEAT_WIDTH + GAP_X);
        const newY = PADDING + row * (SEAT_HEIGHT + GAP_Y);

        // zone_id가 유효한 값일 때만 포함
        const updateData: any = {
          position_x: newX,
          position_y: newY,
          direction: seat.direction || 'south',
          room_id: selectedRoom,
        };

        // zone_id가 있고 빈 문자열이 아닐 때만 추가
        if (seat.zone_id && seat.zone_id !== '') {
          updateData.zone_id = seat.zone_id;
        }

        return layoutRepository.updateSeatPosition(
          typeof seat.seat_number === 'string' ? parseInt(seat.seat_number) : seat.seat_number,
          updateData
        );
      });

      await Promise.all(updatePromises);

      // 그리드 크기 자동 조정
      const newGridWidth = Math.ceil((PADDING * 2 + columns * SEAT_WIDTH + (columns - 1) * GAP_X) / CELL_SIZE);
      const newGridHeight = Math.ceil((PADDING * 2 + rows * SEAT_HEIGHT + (rows - 1) * GAP_Y) / CELL_SIZE);

      await layoutRepository.updateRoomGridSize(selectedRoom, Math.max(newGridWidth, 5), Math.max(newGridHeight, 5));

      alert(`✅ ${totalSeats}개 좌석이 ${columns}열 x ${rows}행으로 재배치되었습니다!`);
      setShowAutoLayoutModal(false);
      await loadAllData();
    } catch (error) {
      console.error('자동 배치 실패:', error);
      alert('❌ 자동 배치에 실패했습니다: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    } finally {
      setAutoLayoutSaving(false);
    }
  };

  // ==================== Architecture Handlers ====================
  const handleAddArchitecture = () => {
    const success = createArchitecture(newArchitecture);
    if (success) {
      setShowArchitectureModal(false);
      setNewArchitecture({
        element_type: 'pillar',
        position_x: 0,
        position_y: 0,
        width: 80,
        height: 80,
        rotation: 0,
        wall_position: 'top',
      });
    }
  };

  const handleEditArchitecture = (element: ArchitectureElement) => {
    setEditingItem({ ...element });
    setEditType('architecture');
    setShowEditModal(true);
  };

  const handleSaveArchitectureEdit = () => {
    if (!editingItem) return;
    const success = updateArchitecture(editingItem.id, editingItem);
    if (success) {
      setShowEditModal(false);
      setEditingItem(null);
      setEditType(null);
    }
  };

  // ==================== Seat Handlers ====================
  const handleCellClick = (x: number, y: number, e: React.MouseEvent) => {
    if (!addSeatMode) return;

    const newPosition = { x, y };

    if (e.shiftKey && lastSelectedPosition) {
      // Shift 키로 범위 선택
      const range = layoutService.getPositionsInRange(lastSelectedPosition, newPosition);
      setSelectedPositions(range);
    } else {
      // 단일 선택/토글
      const isSelected = selectedPositions.some(p => p.x === x && p.y === y);
      if (isSelected) {
        setSelectedPositions(selectedPositions.filter(p => !(p.x === x && p.y === y)));
      } else {
        setSelectedPositions([...selectedPositions, newPosition]);
      }
    }

    setLastSelectedPosition(newPosition);
  };

  const handleCreateSeats = async () => {
    if (selectedPositions.length === 0) {
      alert('좌석을 배치할 위치를 선택해주세요.');
      return;
    }

    // 구역은 선택 사항 (2026-10-04) — 좌석부터 깔고 나중에 구역 목록의 [좌석 지정]으로 넣을 수 있다

    if (!selectedRoom) {
      alert('룸을 선택해주세요.');
      return;
    }

    try {
      let currentSeatNumber = newSeatForm.start_number;

      for (const pos of selectedPositions) {
        const seatData = {
          seat_number: currentSeatNumber,
          room_id: selectedRoom,
          zone_id: newSeatForm.zone_id || null,
          seat_type: newSeatForm.seat_type,
          position_x: pos.x,
          position_y: pos.y,
          direction: newSeatForm.direction,
          attributes: {
            has_power_outlet: true,
            has_desk_lamp: true,
            is_window_seat: false,
            partition_height: 'medium',
            monitor_compatible: false,
            has_storage: false,
          },
        };

        await layoutRepository.createSeat(seatData);
        currentSeatNumber++;
      }

      const addedCount = selectedPositions.length;
      setShowAddSeatModal(false);
      setAddSeatMode(false);
      setSelectedPositions([]);
      setLastSelectedPosition(null);
      setNewSeatForm({
        zone_id: '',
        seat_type: 'standard',
        direction: 'north',
        start_number: 1,
      });
      // 새로 추가된 좌석 포함해서 데이터 다시 로드
      await loadRoomData(selectedRoom);

      // 자동 6열 배치 (사용자가 따로 누르지 않아도 됨)
      try {
        setAutoLayoutColumns(6);
        setAutoLayoutDirection('horizontal');
        // 미세 지연 후 자동 배치 실행 (state 반영 보장)
        await new Promise((resolve) => setTimeout(resolve, 200));
        await handleAutoLayout();
      } catch (e) {
        console.warn('자동 배치 스킵:', e);
      }

      alert(`✅ ${addedCount}개 좌석이 생성되고 6열로 자동 배치되었습니다.`);
    } catch (error) {
      console.error('좌석 생성 실패:', error);

      // 에러 메시지 파싱하여 사용자 친화적인 메시지 표시
      let errorMessage = '좌석 생성에 실패했습니다.';

      if (error instanceof Error) {
        const errorText = error.message;

        // PostgreSQL 중복 키 에러 체크 (code: '23505')
        if (errorText.includes('duplicate key') && errorText.includes('seats_org_seat_number_unique')) {
          // 에러 메시지에서 좌석 번호 추출 시도
          const match = errorText.match(/seat_number\)=\([^,]+,\s*(\d+)\)/);
          const duplicateSeatNumber = match ? match[1] : '알 수 없음';

          errorMessage = `❌ 좌석 번호 ${duplicateSeatNumber}번은 이미 사용 중입니다.\n다른 좌석 번호를 선택해주세요.`;
        } else {
          errorMessage = `좌석 생성에 실패했습니다: ${errorText}`;
        }
      }

      alert(errorMessage);
    }
  };

  // ==================== Collision Detection ====================
  const checkArchitectureCollision = (
    archElement: ArchitectureElement,
    allSeats: Seat[],
    allDevices: Device[]
  ): boolean => {
    // DB에 픽셀 좌표가 저장됨 - CELL_SIZE 곱셈 제거
    const archBox = {
      x1: archElement.position_x,
      y1: archElement.position_y,
      x2: archElement.position_x + CELL_SIZE,
      y2: archElement.position_y + CELL_SIZE,
    };

    // 좌석과 충돌 체크
    for (const seat of allSeats) {
      if (seat.room_id !== selectedRoom) continue;
      if (selectedZoneId && seat.zone_id !== selectedZoneId) continue;

      const seatBox = {
        x1: seat.position_x,
        y1: seat.position_y,
        x2: seat.position_x + CELL_SIZE,
        y2: seat.position_y + CELL_SIZE,
      };

      if (boxesOverlap(archBox, seatBox)) {
        return true;
      }
    }

    // 기기와 충돌 체크
    for (const device of allDevices) {
      if (device.room_id !== selectedRoom) continue;

      const deviceBox = {
        x1: device.position_x,
        y1: device.position_y,
        x2: device.position_x + CELL_SIZE,
        y2: device.position_y + CELL_SIZE,
      };

      if (boxesOverlap(archBox, deviceBox)) {
        return true;
      }
    }

    return false;
  };

  const boxesOverlap = (
    box1: { x1: number; y1: number; x2: number; y2: number },
    box2: { x1: number; y1: number; x2: number; y2: number }
  ): boolean => {
    return !(box1.x2 <= box2.x1 || box1.x1 >= box2.x2 || box1.y2 <= box2.y1 || box1.y1 >= box2.y2);
  };

  // ==================== Seat Collision Detection ====================
  const checkSeatCollision = (
    movingSeat: Seat,
    newX: number,
    newY: number,
    allSeats: Seat[]
  ): Seat | null => {
    const movingBox = {
      x1: newX,
      y1: newY,
      x2: newX + CELL_SIZE - 4, // 약간의 여유 (4px)
      y2: newY + CELL_SIZE - 4,
    };

    for (const seat of allSeats) {
      // 자기 자신은 제외
      if (seat.seat_number === movingSeat.seat_number) continue;
      // 다른 룸은 제외
      if (seat.room_id !== selectedRoom) continue;

      const seatBox = {
        x1: seat.position_x,
        y1: seat.position_y,
        x2: seat.position_x + CELL_SIZE - 4,
        y2: seat.position_y + CELL_SIZE - 4,
      };

      if (boxesOverlap(movingBox, seatBox)) {
        return seat; // 충돌하는 좌석 반환
      }
    }

    return null; // 충돌 없음
  };

  // 가장 가까운 빈 위치 찾기
  const findNearestEmptyPosition = (
    targetX: number,
    targetY: number,
    movingSeat: Seat,
    allSeats: Seat[],
    gridWidth: number,
    gridHeight: number
  ): { x: number; y: number } => {
    const maxX = gridWidth * CELL_SIZE - CELL_SIZE;
    const maxY = gridHeight * CELL_SIZE - CELL_SIZE;

    // 나선형으로 주변 탐색 (CELL_SIZE 단위)
    for (let radius = CELL_SIZE; radius <= CELL_SIZE * 5; radius += CELL_SIZE) {
      for (let dx = -radius; dx <= radius; dx += CELL_SIZE) {
        for (let dy = -radius; dy <= radius; dy += CELL_SIZE) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue; // 테두리만 검사

          const newX = Math.max(0, Math.min(targetX + dx, maxX));
          const newY = Math.max(0, Math.min(targetY + dy, maxY));

          if (!checkSeatCollision(movingSeat, newX, newY, allSeats)) {
            return { x: newX, y: newY };
          }
        }
      }
    }

    // 빈 위치를 못 찾으면 원래 위치 반환
    return { x: movingSeat.position_x, y: movingSeat.position_y };
  };

  // ==================== 공유 SeatItem을 사용한 좌석 렌더링 ====================
  // 좌석 드래그 시작 핸들러
  const handleSeatMouseDown = useCallback((seat: Seat, e: React.MouseEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();

    const seatElement = e.currentTarget as HTMLElement;
    const seatRect = seatElement.getBoundingClientRect();
    const offsetX = e.clientX - seatRect.left;
    const offsetY = e.clientY - seatRect.top;

    dragStartRef.current = {
      seatNumber: seat.seat_number,
      startX: e.clientX,
      startY: e.clientY,
      originalX: seat.position_x,
      originalY: seat.position_y,
      offsetX,
      offsetY,
      hasMoved: false,
    };

    setDraggingSeat({
      seatNumber: seat.seat_number,
      x: seat.position_x,
      y: seat.position_y,
      isColliding: false,
      errorMessage: '',
    });
  }, [isEditMode]);

  // Seat → CanvasSeatData 변환 함수
  const convertToCanvasSeat = useCallback((seat: Seat): CanvasSeatData => ({
    seat_number: seat.seat_number,
    position_x: seat.position_x,
    position_y: seat.position_y,
    zone_id: seat.zone_id,
    is_occupied: seat.is_occupied,
    is_available: seat.is_available,
    is_active: seat.is_active,
  }), []);

  // 좌석 렌더링 함수 (공유 SeatItem 사용)
  const renderSeat = useCallback((seat: Seat, isHighlighted: boolean, isDimmed: boolean) => {
    const zone = zones.find(z => z.id === seat.zone_id);
    const zoneColor = zone?.color || '#3B82F6';
    const isBeingDragged = draggingSeat?.seatNumber === seat.seat_number;

    return (
      <SeatItem
        key={seat.seat_number}
        seat={convertToCanvasSeat(seat)}
        mode="edit"
        isHighlighted={isHighlighted}
        isDimmed={isDimmed}
        zoneColor={zoneColor}
        zoneAccent={zone?.color || null}
        zoneLabel={zone?.name || null}
        isEditMode={isEditMode}
        isBeingDragged={isBeingDragged}
        displayX={isBeingDragged ? draggingSeat?.x : undefined}
        displayY={isBeingDragged ? draggingSeat?.y : undefined}
        isColliding={isBeingDragged ? draggingSeat?.isColliding : false}
        errorMessage={isBeingDragged ? draggingSeat?.errorMessage : ''}
        onMouseDown={(e) => handleSeatMouseDown(seat, e)}
        onClick={() => !isBeingDragged && !isEditMode && setSelectedItem({ type: 'seat', id: seat.seat_number })}
      />
    );
  }, [zones, draggingSeat, isEditMode, convertToCanvasSeat, handleSeatMouseDown, setSelectedItem]);

  interface DraggableArchitectureProps {
    element: ArchitectureElement;
  }

  const DraggableArchitecture: React.FC<DraggableArchitectureProps> = ({ element }) => {
    const [{ isDragging }, drag] = useDrag({
      type: 'architecture',
      item: { type: 'architecture', data: element } as DraggableItem,
      canDrag: () => isEditMode, // 편집 모드일 때만 드래그 가능
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    // 충돌 체크
    const hasCollision = checkArchitectureCollision(element, seats, devices);

    return (
      <div
        ref={isEditMode ? drag : null}
        className={`absolute transition-all ${isEditMode ? 'cursor-move' : 'cursor-pointer'}`}
        style={{
          left: `${element.position_x}px`,  // DB에 픽셀 좌표가 저장됨
          top: `${element.position_y}px`,
          width: `${CELL_SIZE}px`,
          height: `${CELL_SIZE}px`,
          transform: `rotate(${element.rotation}deg)`,
          opacity: isDragging ? 0.5 : 1,
          zIndex: 20, // 좌석(1), 기기보다 위에 표시
          border: hasCollision ? '3px solid #EF4444' : '2px solid #10B981',
          boxShadow: hasCollision
            ? '0 0 10px rgba(239, 68, 68, 0.5)'
            : '0 0 10px rgba(16, 185, 129, 0.3)',
        }}
        onClick={() => setSelectedItem({ type: 'architecture', id: element.id })}
      >
        {renderArchitectureElement(element)}
        {hasCollision && (
          <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl">
            ⚠️ 충돌
          </div>
        )}
      </div>
    );
  };

  interface DroppableGridCellProps {
    x: number;
    y: number;
    children?: React.ReactNode;
  }

  const DroppableGridCell: React.FC<DroppableGridCellProps> = ({ x, y, children }) => {
    // 그리드 셀은 배경으로만 사용 (드롭은 컨테이너에서 처리)
    const isSelected = selectedPositions.some(p => p.x === x && p.y === y);

    return (
      <div
        className={`border border-gray-200 ${
          isSelected ? 'bg-yellow-100' : 'bg-white'
        }`}
        style={{
          width: `${CELL_SIZE}px`,
          height: `${CELL_SIZE}px`,
        }}
        onClick={(e) => handleCellClick(x, y, e)}
      >
        {children}
      </div>
    );
  };

  // ==================== 드래그 스냅 설정 ====================
  const SNAP_SIZE = 16; // 0.2칸 = 16px 스냅

  interface GridContainerProps {
    children: React.ReactNode;
    gridWidth: number;
    gridHeight: number;
  }

  const GridContainer: React.FC<GridContainerProps> = ({
    children,
    gridWidth,
    gridHeight,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // 마우스 이동 핸들러 (드래그 중)
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      if (!draggingSeat || !containerRef.current || !dragStartRef.current) return;

      // 최소 이동 거리 체크 (클릭과 드래그 구분)
      const dx = e.clientX - dragStartRef.current.startX;
      const dy = e.clientY - dragStartRef.current.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // 최소 거리 이상 움직여야 실제 드래그로 인정
      if (distance < MIN_DRAG_DISTANCE && !dragStartRef.current.hasMoved) {
        return; // 아직 드래그 시작 안함 - 원래 위치 유지
      }

      const containerRect = containerRef.current.getBoundingClientRect();

      // 오프셋을 빼고 스케일 보정 (좌석 내 클릭 위치 유지)
      // offsetX/Y는 스크린 픽셀이므로 함께 스케일 나눔
      let rawX = (e.clientX - containerRect.left - dragStartRef.current.offsetX) / scale;
      let rawY = (e.clientY - containerRect.top - dragStartRef.current.offsetY) / scale;

      // 첫 드래그 시작 시 로그
      if (!dragStartRef.current.hasMoved) {
        console.log('🔄 [handleMouseMove] 드래그 시작:', {
          mouseClient: { x: e.clientX, y: e.clientY },
          containerRect: { left: containerRect.left, top: containerRect.top },
          offset: { x: dragStartRef.current.offsetX, y: dragStartRef.current.offsetY },
          raw: { x: rawX, y: rawY },
          scale,
        });
      }

      // 드래그 시작됨
      dragStartRef.current.hasMoved = true;

      // 스냅 적용
      let x = Math.round(rawX / SNAP_SIZE) * SNAP_SIZE;
      let y = Math.round(rawY / SNAP_SIZE) * SNAP_SIZE;

      // 범위 계산
      const maxX = gridWidth * CELL_SIZE - CELL_SIZE;
      const maxY = gridHeight * CELL_SIZE - CELL_SIZE;

      // 범위 밖 체크
      const isOutOfBounds = x < 0 || y < 0 || x > maxX || y > maxY;

      // 범위 제한
      const displayX = Math.max(0, Math.min(x, maxX));
      const displayY = Math.max(0, Math.min(y, maxY));

      // 충돌 감지
      let isColliding = false;
      let errorMessage = '';

      if (isOutOfBounds) {
        isColliding = true;
        errorMessage = '범위 밖';
      } else {
        // 현재 드래그 중인 좌석 찾기
        const movingSeat = seats.find(s => s.seat_number === draggingSeat.seatNumber);
        if (movingSeat) {
          const collision = checkSeatCollision(movingSeat, displayX, displayY, seats);
          if (collision) {
            isColliding = true;
            errorMessage = `${collision.seat_number}번과 겹침`;
          }
        }
      }

      setDraggingSeat({
        seatNumber: draggingSeat.seatNumber,
        x: displayX,
        y: displayY,
        isColliding,
        errorMessage,
      });
    }, [draggingSeat, gridWidth, gridHeight, scale, seats]);

    // 마우스 업 핸들러 (드롭)
    const handleMouseUp = useCallback(() => {
      if (!draggingSeat) {
        dragStartRef.current = null;
        return;
      }

      // 실제 드래그가 발생하지 않았으면 (클릭만) 아무것도 하지 않음
      if (!dragStartRef.current?.hasMoved) {
        console.log('📌 클릭만 감지됨 - 위치 변경 없음');
        setDraggingSeat(null);
        dragStartRef.current = null;
        return;
      }

      // 충돌 중이면 원위치
      if (draggingSeat.isColliding) {
        console.log('❌ 충돌로 인해 원위치');
        setDraggingSeat(null);
        dragStartRef.current = null;
        return;
      }

      // 위치가 실제로 변경되었는지 확인
      const originalX = dragStartRef.current?.originalX ?? 0;
      const originalY = dragStartRef.current?.originalY ?? 0;

      if (draggingSeat.x === originalX && draggingSeat.y === originalY) {
        console.log('📍 위치 변경 없음 (같은 위치)');
        setDraggingSeat(null);
        dragStartRef.current = null;
        return;
      }

      // 위치 업데이트
      const movingSeat = seats.find(s => s.seat_number === draggingSeat.seatNumber);
      if (movingSeat) {
        console.log(`✅ 좌석 ${movingSeat.seat_number} 위치 변경: (${originalX}, ${originalY}) → (${draggingSeat.x}, ${draggingSeat.y})`);
        handleDrop(
          { type: 'seat', data: movingSeat },
          { x: draggingSeat.x, y: draggingSeat.y }
        );
        setHasUnsavedChanges(true);
      }

      setDraggingSeat(null);
      dragStartRef.current = null;
    }, [draggingSeat, seats, handleDrop]);

    // 마우스가 컨테이너 밖으로 나가면 드래그 취소
    const handleMouseLeave = useCallback(() => {
      if (draggingSeat) {
        setDraggingSeat(null);
        dragStartRef.current = null;
      }
    }, [draggingSeat]);

    // 세밀한 눈금선 (0.2칸 = 16px 단위 - 스냅 크기와 동일)
    const MINOR_GRID = SNAP_SIZE; // 0.2칸 = 16px
    const totalWidth = gridWidth * CELL_SIZE;
    const totalHeight = gridHeight * CELL_SIZE;

    return (
      <div
        ref={containerRef}
        id="grid-container"
        className="relative"
        onMouseMove={isEditMode ? handleMouseMove : undefined}
        onMouseUp={isEditMode ? handleMouseUp : undefined}
        onMouseLeave={isEditMode ? handleMouseLeave : undefined}
        style={{
          width: `${totalWidth}px`,
          height: `${totalHeight}px`,
          backgroundColor: draggingSeat && isEditMode ? 'rgba(59, 130, 246, 0.1)' : '#fafafa',
        }}
      >
        {/* 세밀한 눈금선 (0.25칸 단위) - 편집 모드에서만 */}
        {isEditMode && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width={totalWidth}
            height={totalHeight}
            style={{ zIndex: 0 }}
          >
            {/* 세로 눈금선 */}
            {Array.from({ length: Math.floor(totalWidth / MINOR_GRID) + 1 }).map((_, i) => {
              const x = i * MINOR_GRID;
              const isMajor = x % CELL_SIZE === 0;
              return (
                <line
                  key={`v-${i}`}
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={totalHeight}
                  stroke={isMajor ? '#9CA3AF' : '#E5E7EB'}
                  strokeWidth={isMajor ? 1.5 : 0.5}
                />
              );
            })}
            {/* 가로 눈금선 */}
            {Array.from({ length: Math.floor(totalHeight / MINOR_GRID) + 1 }).map((_, i) => {
              const y = i * MINOR_GRID;
              const isMajor = y % CELL_SIZE === 0;
              return (
                <line
                  key={`h-${i}`}
                  x1={0}
                  y1={y}
                  x2={totalWidth}
                  y2={y}
                  stroke={isMajor ? '#9CA3AF' : '#E5E7EB'}
                  strokeWidth={isMajor ? 1.5 : 0.5}
                />
              );
            })}
            {/* 좌표 레이블 (100px 단위) */}
            {Array.from({ length: Math.floor(totalWidth / 100) + 1 }).map((_, i) => (
              <text
                key={`lx-${i}`}
                x={i * 100 + 2}
                y={12}
                fontSize={10}
                fill="#6B7280"
              >
                {i * 100}
              </text>
            ))}
            {Array.from({ length: Math.floor(totalHeight / 100) + 1 }).map((_, i) => (
              i > 0 && (
                <text
                  key={`ly-${i}`}
                  x={2}
                  y={i * 100 + 12}
                  fontSize={10}
                  fill="#6B7280"
                >
                  {i * 100}
                </text>
              )
            ))}
          </svg>
        )}
        {/* 기존 그리드 셀 */}
        <div
          className="relative grid gap-0"
          style={{
            gridTemplateColumns: `repeat(${gridWidth}, ${CELL_SIZE}px)`,
            gridTemplateRows: `repeat(${gridHeight}, ${CELL_SIZE}px)`,
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  // ==================== Render ====================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      {/* 드래그 미리보기 레이어 - 편집모드에서만 표시 */}
      {/* 직접 드래그 구현 - DragPositionTracker 제거됨 */}
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-4 overflow-y-auto max-h-[40vh]">
          {/* Edit Mode Status Banner */}
          {isEditMode && (
            <div className="mb-3 bg-blue-100 border-2 border-blue-500 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">✏️</span>
                <span className="text-blue-900 font-bold text-lg">편집 모드 활성화 - 드래그하여 좌석을 이동할 수 있습니다</span>
              </div>
            </div>
          )}
          {!isEditMode && (
            <div className="mb-3 bg-gray-100 border-2 border-gray-300 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🔒</span>
                <span className="text-gray-700 font-bold text-lg">읽기 전용 모드 - 편집하려면 "편집 모드" 버튼을 클릭하세요</span>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {/* 첫 번째 줄: 제목과 층 선택 */}
            <div className="flex flex-wrap items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">통합 레이아웃 관리</h1>

              {/* Room Selector */}
              <div className="flex items-center space-x-2">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-500" />
                <div className="relative">
                  <select
                    value={selectedRoom || ''}
                    onChange={(e) => setSelectedRoom(e.target.value)}
                    className="appearance-none bg-white border-2 border-blue-500 rounded-lg py-2 px-4 pr-10 font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">층 선택</option>
                    {rooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-2 top-3 h-5 w-5 text-blue-700 pointer-events-none" />
                </div>

                {/* 룸 추가 버튼 - 편집 모드일 때만 표시 */}
                {isEditMode && (
                  <>
                    <button
                      onClick={() => {
                        setRoomForm({ name: '', description: '', grid_width: 20, grid_height: 15 });
                        setShowRoomModal(true);
                      }}
                      className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      title="룸 추가"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">룸 추가</span>
                    </button>
                    {rooms.length > 1 && (
                      <button
                        onClick={() => setShowRoomOrderModal(true)}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        title="룸 순서 변경"
                      >
                        <Bars3Icon className="h-5 w-5" />
                        <span className="text-sm font-medium">순서</span>
                      </button>
                    )}
                    {/* 선택된 룸 수정/삭제 */}
                    {selectedRoom && (
                      <>
                        <button
                          onClick={() => {
                            const room = rooms.find(r => r.id === selectedRoom);
                            if (room) {
                              setRoomEditForm({ name: room.name, description: room.description || '' });
                              setShowRoomEditModal(true);
                            }
                          }}
                          className="flex items-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                          title="룸 이름 수정"
                        >
                          <PencilIcon className="h-5 w-5" />
                          <span className="text-sm font-medium">수정</span>
                        </button>
                        <button
                          onClick={() => setShowRoomDeleteConfirm(true)}
                          className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          title="룸 삭제"
                        >
                          <TrashIcon className="h-5 w-5" />
                          <span className="text-sm font-medium">삭제</span>
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 두 번째 줄: 툴바 버튼들 */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Zoom Controls */}
              <button
                onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                className="p-2 hover:bg-gray-100 rounded-md"
                title="축소"
              >
                <MagnifyingGlassMinusIcon className="h-5 w-5" />
              </button>
              <span className="text-sm text-gray-600">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale(Math.min(2, scale + 0.1))}
                className="p-2 hover:bg-gray-100 rounded-md"
                title="확대"
              >
                <MagnifyingGlassPlusIcon className="h-5 w-5" />
              </button>
              <button
                onClick={fitToScreen}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200"
                title="화면에 맞춤"
              >
                📐 맞춤
              </button>

              {/* View Mode */}
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as any)}
                className="border border-gray-300 rounded-md py-2 px-3"
              >
                <option value="all">전체 보기</option>
                <option value="seats">좌석만</option>
                <option value="devices">기기만</option>
              </select>

              {/* Edit Mode Toggle */}
              <button
                onClick={() => {
                  const newMode = !isEditMode;
                  setIsEditMode(newMode);
                }}
                className={`px-4 py-2 rounded-md font-bold ${
                  isEditMode ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {isEditMode ? '✅ 편집 중' : '편집 모드'}
              </button>

              {/* Grid Size Setting Button - 편집 모드에서만 표시 */}
              {isEditMode && (
                <button
                  onClick={openGridSizeModal}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-1"
                  title="그리드 크기 설정"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                  <span>그리드 크기</span>
                </button>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveLayout}
                disabled={saving}
                className={`px-4 py-2 text-white rounded-md flex items-center space-x-2 ${
                  saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {saving && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{saving ? '저장 중...' : '저장'}</span>
              </button>

              {/* 구역 목록 토글 버튼 - lg 이상에서만 표시 */}
              <button
                onClick={() => setShowZoneSidebar(!showZoneSidebar)}
                className={`hidden lg:flex px-3 py-2 rounded-md items-center space-x-1 ${
                  showZoneSidebar ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-700'
                }`}
                title="구역 목록 표시/숨김"
              >
                <span className="text-sm">{showZoneSidebar ? '◀' : '▶'}</span>
                <span className="text-sm">구역 목록</span>
              </button>
            </div>
          </div>
          </div>

          {/* Action Buttons Row */}
          {isEditMode && (
            <div className="flex items-center space-x-2 mt-4 px-4">
              <button
                onClick={() => openZoneModal()}
                className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                구역 추가
              </button>

              <button
                onClick={() => setShowArchitectureModal(true)}
                className="flex items-center px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
              >
                <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                건축 요소 추가
              </button>

              {/* 자동 배치 버튼 */}
              {seats.length > 0 && (
                <button
                  onClick={() => {
                    setAutoLayoutColumns(Math.min(6, seats.length));
                    setShowAutoLayoutModal(true);
                  }}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-1" />
                  자동 배치
                </button>
              )}
            </div>
          )}

          {/* Selected Item Info */}
          {selectedItem && isEditMode && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
              <span className="text-sm text-blue-900">
                선택됨: {selectedItem.type === 'seat' ? `좌석 ${selectedItem.id}번` : `${selectedItem.type} ${selectedItem.id}`}
              </span>
              <div className="space-x-2">
                {selectedItem.type === 'seat' && (
                  <button
                    onClick={() => {
                      const seat = seats.find(s => s.seat_number === selectedItem.id);
                      if (seat) {
                        setSelectedSeatForEdit(seat);
                        setSeatAttributesForm({
                          seat_type: seat.seat_type || 'standard',
                          attributes: seat.attributes || seatAttributesForm.attributes,
                        });
                        setShowSeatAttributesModal(true);
                      }
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    <Cog6ToothIcon className="h-4 w-4 inline mr-1" />
                    속성 설정
                  </button>
                )}
                {selectedItem.type === 'device' && (
                  <button
                    onClick={() => {
                      const device = devices.find(d => d.id === selectedItem.id);
                      if (device) handleEditDevice(device);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    수정
                  </button>
                )}
                {selectedItem.type === 'architecture' && (
                  <button
                    onClick={() => {
                      const element = architectureElements.find(e => e.id === selectedItem.id);
                      if (element) handleEditArchitecture(element);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    수정
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (selectedItem.type === 'seat') {
                      await deleteSeat(selectedItem.id as number);
                    } else if (selectedItem.type === 'device') {
                      await deleteDevice(selectedItem.id as string);
                    } else if (selectedItem.type === 'architecture') {
                      deleteArchitecture(selectedItem.id as string);
                    }
                  }}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  삭제
                </button>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                >
                  취소
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div ref={gridContainerRef} className="flex-1 overflow-auto p-6">
            {!currentRoom ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 text-lg">룸을 선택해주세요</p>
              </div>
            ) : (
              <div
                className="inline-block bg-white shadow-lg rounded-lg border border-gray-300"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                }}
              >
              {/* Grid */}
              <GridContainer gridWidth={currentRoom.grid_width} gridHeight={currentRoom.grid_height}>
                {/* Grid Cells */}
                {Array.from({ length: currentRoom.grid_height }).map((_, y) =>
                  Array.from({ length: currentRoom.grid_width }).map((_, x) => (
                    <DroppableGridCell key={`${x}-${y}`} x={x} y={y} />
                  ))
                )}

                {/* Seats - 공유 SeatItem 사용 */}
                {(viewMode === 'all' || viewMode === 'seats') &&
                  (Array.isArray(seats) ? seats : [])
                    .filter(seat => !seat.room_id || seat.room_id === selectedRoom)
                    .map((seat) => {
                      const isHighlighted = !!(selectedZoneId && seat.zone_id === selectedZoneId);
                      const isDimmed = !!(selectedZoneId && seat.zone_id !== selectedZoneId);
                      return renderSeat(seat, isHighlighted, isDimmed);
                    })}

                {/* Devices */}
                {(viewMode === 'all' || viewMode === 'devices') &&
                  (Array.isArray(devices) ? devices : [])
                    .filter(device => !device.room_id || device.room_id === selectedRoom)
                    .map((device) => (
                      <div
                        key={device.id}
                        className="absolute cursor-pointer"
                        style={{
                          left: `${device.position_x}px`,  // DB에 픽셀 좌표가 저장됨
                          top: `${device.position_y}px`,
                          width: `${CELL_SIZE}px`,
                          height: `${CELL_SIZE}px`,
                        }}
                        onClick={() => setSelectedItem({ type: 'device', id: device.id })}
                      >
                        <div
                          className={`w-full h-full rounded-lg flex flex-col items-center justify-center text-white shadow-lg ${layoutService.getDeviceStatusColor(
                            device.status
                          )}`}
                        >
                          <span className="text-2xl">{layoutService.getDeviceTypeIcon(device.device_type)}</span>
                          <span className="text-xs text-center px-1">{device.device_name}</span>
                        </div>
                      </div>
                    ))}

                {/* Architecture Elements */}
                {viewMode === 'all' &&
                  (Array.isArray(architectureElements) ? architectureElements : [])
                    .filter(element => !element.room_id || element.room_id === selectedRoom)
                    .map((element) => (
                      <DraggableArchitecture key={element.id} element={element} />
                    ))}
              </GridContainer>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Zone List - 작은 화면에서는 숨김 */}
        {showZoneSidebar && (
          <div className="hidden lg:flex w-64 flex-shrink-0 bg-white border-l border-gray-200 shadow-lg overflow-y-auto flex-col p-4 space-y-3">
          <div className="text-sm font-bold text-gray-700 mb-3 pb-2 border-b">구역 목록</div>
          {zones.length === 0 ? (
            <div className="text-xs text-gray-400 text-center">없음</div>
          ) : (
            zones.map((zone) => (
              <div
                key={zone.id}
                className={`relative group cursor-pointer transition-all p-3 rounded-lg hover:bg-gray-50 ${
                  selectedZoneId === zone.id ? 'bg-blue-50 border-2 border-blue-500' : 'border-2 border-transparent'
                }`}
                onClick={() => setSelectedZoneId(selectedZoneId === zone.id ? null : zone.id)}
              >
                {/* Zone Info */}
                <div className="flex items-center space-x-3">
                  {/* Zone Color Circle */}
                  <div
                    className="w-12 h-12 rounded-full border-2 flex items-center justify-center shadow-md flex-shrink-0"
                    style={{ backgroundColor: zone.color, borderColor: zone.color }}
                  >
                    <span className={`text-lg font-bold ${isLightColor(zone.color) ? 'text-gray-800' : 'text-white drop-shadow-md'}`}>
                      {zone.name.charAt(0)}
                    </span>
                  </div>

                  {/* Zone Details */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{zone.name}</div>
                    {zone.room_id && selectedRoom && zone.room_id !== selectedRoom && (
                      <div className="text-xs text-amber-700">다른 방 구역 (이 방 좌석 포함)</div>
                    )}
                    {zone.description && (
                      <div className="text-xs text-gray-500 truncate">{zone.description}</div>
                    )}
                    <div className="text-xs text-gray-600 mt-1">
                      좌석 {zone.seat_count ?? 0}개
                      {zone.has_patrol_qr && <span className="ml-2 text-blue-700">🚶 순찰 QR</span>}
                    </div>
                  </div>
                </div>

                {/* Edit Mode Actions */}
                {isEditMode && (
                  <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSeatZone(zone);
                      }}
                      className="flex-1 px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 flex items-center justify-center"
                      title="이 구역에 들어갈 좌석 고르기"
                    >
                      🪑 좌석 지정
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openZoneModal(zone);
                      }}
                      className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center justify-center"
                      title="수정"
                    >
                      ✏️ 수정
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // 두 번 눌러 삭제 — window.confirm 은 환경에 따라 막혀 '반응 없음'으로 보인다
                        if (confirmDeleteZoneId === zone.id) {
                          setConfirmDeleteZoneId(null);
                          deleteZone(zone.id);
                        } else {
                          setConfirmDeleteZoneId(zone.id);
                        }
                      }}
                      className="flex-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center justify-center"
                      title="삭제 (좌석은 지워지지 않고 '구역 없음'으로 남습니다)"
                    >
                      {confirmDeleteZoneId === zone.id ? '정말 삭제' : '🗑️ 삭제'}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
          </div>
        )}

        {/* Zone Modal */}
        {seatZone && (
          <ZoneSeatsModal
            zone={{ id: String(seatZone.id), name: seatZone.name, color: seatZone.color }}
            onClose={() => setSeatZone(null)}
            onSaved={async () => {
              setSeatZone(null);
              if (selectedRoom) await loadRoomData(selectedRoom);
            }}
          />
        )}
        {showZoneModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {editingZone ? '구역 수정' : '새 구역 추가'}
                </h3>
                <button
                  onClick={() => setShowZoneModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleZoneSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    구역 이름
                  </label>
                  <input
                    type="text"
                    value={zoneForm.name}
                    onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: A구역 (프리미엄)"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    설명
                  </label>
                  <textarea
                    value={zoneForm.description}
                    onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="구역에 대한 설명"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    색상
                  </label>
                  {[
                    { label: '연한 색 (출석부에서 미출석·면제와 헷갈리지 않음)', items: ZONE_COLORS.filter((c) => c.light) },
                    { label: '진한 색', items: ZONE_COLORS.filter((c) => !c.light) },
                  ].map((group) => (
                    <div key={group.label} className="mb-3">
                      <div className="text-xs text-gray-500 mb-1">{group.label}</div>
                      <div className="grid grid-cols-5 gap-2">
                        {group.items.map((colorOption) => (
                          <button
                            key={colorOption.value}
                            type="button"
                            onClick={() => setZoneForm({ ...zoneForm, color: colorOption.value })}
                            className={`w-12 h-12 rounded-lg border-2 transition-all hover:scale-105 ${
                              zoneForm.color === colorOption.value
                                ? 'border-gray-800 ring-2 ring-blue-500 ring-offset-2'
                                : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: colorOption.value }}
                            title={colorOption.name}
                          >
                            {zoneForm.color === colorOption.value && (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className={`text-lg font-bold ${isLightColor(colorOption.value) ? 'text-gray-800' : 'text-white drop-shadow-lg'}`}>✓</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    빨강·보라는 출석부에서 미출석·출석 면제 표시와 헷갈릴 수 있습니다.
                  </p>
                </div>

                <div>
                  {/* 2026-10-12 대표 지시 — 설명 추가. 이 값은 저장·표시만 되고 요금 계산에 쓰는 곳이 없다(코드 확인). */}
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    프리미엄 배율
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      구역별 요금 가중치 메모용 (예: 1.2 = 20% 비싼 구역). 현재 이용권 요금 계산에는 쓰이지 않아 1로 두셔도 됩니다.
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5.0"
                    value={zoneForm.premium_rate}
                    onChange={(e) => setZoneForm({ ...zoneForm, premium_rate: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowZoneModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingZone ? '수정' : '생성'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Device Modal */}
        {showDeviceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">새 기기 추가</h3>
                <button
                  onClick={() => setShowDeviceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    기기 타입
                  </label>
                  <select
                    value={newDevice.device_type}
                    onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="face_recognition_gate">얼굴인식 게이트</option>
                    <option value="kiosk">키오스크</option>
                    <option value="printer">프린터</option>
                    <option value="air_purifier">공기청정기</option>
                    <option value="cctv">CCTV</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    기기명
                  </label>
                  <input
                    type="text"
                    value={newDevice.device_name}
                    onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="예: 정문 게이트"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      X 위치
                    </label>
                    <input
                      type="number"
                      value={newDevice.position_x}
                      onChange={(e) => setNewDevice({ ...newDevice, position_x: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Y 위치
                    </label>
                    <input
                      type="number"
                      value={newDevice.position_y}
                      onChange={(e) => setNewDevice({ ...newDevice, position_y: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowDeviceModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleAddDevice}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    추가
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Seat Modal */}
        {showAddSeatModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">좌석 생성</h3>
                <button
                  onClick={() => setShowAddSeatModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900">
                    선택한 위치: {selectedPositions.length}개
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    구역 (선택)
                  </label>
                  <select
                    value={newSeatForm.zone_id}
                    onChange={(e) => setNewSeatForm({ ...newSeatForm, zone_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">구역 없음 (나중에 지정)</option>
                    {zones.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    좌석 타입
                  </label>
                  <select
                    value={newSeatForm.seat_type}
                    onChange={(e) => setNewSeatForm({ ...newSeatForm, seat_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="standard">일반석</option>
                    <option value="premium">프리미엄석</option>
                    <option value="deluxe">디럭스석</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    방향
                  </label>
                  <select
                    value={newSeatForm.direction}
                    onChange={(e) => setNewSeatForm({ ...newSeatForm, direction: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="north">북쪽 (↑)</option>
                    <option value="south">남쪽 (↓)</option>
                    <option value="east">동쪽 (→)</option>
                    <option value="west">서쪽 (←)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작 좌석 번호
                  </label>
                  <input
                    type="number"
                    value={newSeatForm.start_number}
                    onChange={(e) => setNewSeatForm({ ...newSeatForm, start_number: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    좌석이 {newSeatForm.start_number}번부터 {newSeatForm.start_number + selectedPositions.length - 1}번까지 생성됩니다.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowAddSeatModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleCreateSeats}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    생성
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Architecture Modal */}
        {showArchitectureModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">건축 요소 추가</h3>
                <button
                  onClick={() => setShowArchitectureModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    요소 타입
                  </label>
                  <select
                    value={newArchitecture.element_type}
                    onChange={(e) => setNewArchitecture({ ...newArchitecture, element_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="entrance">출입구</option>
                    <option value="entrance_with_gate">게이트 포함 출입구</option>
                    <option value="emergency_exit">비상구</option>
                    <option value="window">창문</option>
                    <option value="pillar">기둥</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      가로 위치 (열)
                    </label>
                    <input
                      type="number"
                      value={newArchitecture.position_x}
                      onChange={(e) => setNewArchitecture({ ...newArchitecture, position_x: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                      max={currentRoom?.grid_width ? currentRoom.grid_width - 1 : 50}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      0~{currentRoom ? currentRoom.grid_width - 1 : 50}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      세로 위치 (행)
                    </label>
                    <input
                      type="number"
                      value={newArchitecture.position_y}
                      onChange={(e) => setNewArchitecture({ ...newArchitecture, position_y: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                      max={currentRoom?.grid_height ? currentRoom.grid_height - 1 : 50}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      0~{currentRoom ? currentRoom.grid_height - 1 : 50}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowArchitectureModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleAddArchitecture}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    추가
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal (Device/Architecture) */}
        {showEditModal && editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {editType === 'device' ? '기기 수정' : '건축 요소 수정'}
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {editType === 'device' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        기기명
                      </label>
                      <input
                        type="text"
                        value={editingItem.device_name || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, device_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={editType === 'device' ? handleSaveDeviceEdit : handleSaveArchitectureEdit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Seat Attributes Modal - Simplified */}
        {showSeatAttributesModal && selectedSeatForEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  좌석 {selectedSeatForEdit.seat_number}번 속성 설정
                </h3>
                <button
                  onClick={() => setShowSeatAttributesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    좌석 타입
                  </label>
                  <select
                    value={seatAttributesForm.seat_type}
                    onChange={(e) => setSeatAttributesForm({ ...seatAttributesForm, seat_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="standard">일반석</option>
                    <option value="premium">프리미엄석</option>
                    <option value="deluxe">디럭스석</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    좌석 옵션
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seatAttributesForm.attributes.has_power_outlet}
                        onChange={(e) => setSeatAttributesForm({
                          ...seatAttributesForm,
                          attributes: { ...seatAttributesForm.attributes, has_power_outlet: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm">🔌 콘센트</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seatAttributesForm.attributes.has_desk_lamp}
                        onChange={(e) => setSeatAttributesForm({
                          ...seatAttributesForm,
                          attributes: { ...seatAttributesForm.attributes, has_desk_lamp: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm">💡 독서등</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seatAttributesForm.attributes.is_window_seat}
                        onChange={(e) => setSeatAttributesForm({
                          ...seatAttributesForm,
                          attributes: { ...seatAttributesForm.attributes, is_window_seat: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm">🪟 창가</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seatAttributesForm.attributes.monitor_compatible}
                        onChange={(e) => setSeatAttributesForm({
                          ...seatAttributesForm,
                          attributes: { ...seatAttributesForm.attributes, monitor_compatible: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm">🖥️ 모니터</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seatAttributesForm.attributes.has_storage}
                        onChange={(e) => setSeatAttributesForm({
                          ...seatAttributesForm,
                          attributes: { ...seatAttributesForm.attributes, has_storage: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm">📦 수납공간</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowSeatAttributesModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Save seat attributes via API
                      console.log('좌석 속성 저장:', {
                        seat_number: selectedSeatForEdit.seat_number,
                        ...seatAttributesForm,
                      });
                      setShowSeatAttributesModal(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid Size Modal - 미터 입력 방식 */}
        {showGridSizeModal && currentRoom && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">📐 열람실 크기 변경</h3>
                <button
                  onClick={() => setShowGridSizeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* 현재 크기 표시 */}
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900">
                    📍 현재 크기: <strong>{currentRoom.grid_width * METERS_PER_CELL}m × {currentRoom.grid_height * METERS_PER_CELL}m</strong>
                    <span className="text-blue-600 ml-2">
                      ({((currentRoom.grid_width * METERS_PER_CELL) * (currentRoom.grid_height * METERS_PER_CELL)).toFixed(1)}㎡ / 약 {(((currentRoom.grid_width * METERS_PER_CELL) * (currentRoom.grid_height * METERS_PER_CELL)) / 3.3).toFixed(1)}평)
                    </span>
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    ⚠️ 크기를 줄이면 범위를 벗어난 좌석이 삭제될 수 있습니다.
                  </p>
                </div>

                {/* 미터 단위 입력 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      가로 (미터) *
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        step="0.5"
                        min="2.5"
                        max="25"
                        value={gridSizeRealForm.width_meters}
                        onChange={(e) => {
                          const meters = parseFloat(e.target.value) || 0;
                          setGridSizeRealForm({ ...gridSizeRealForm, width_meters: meters });
                          setGridSizeForm({ ...gridSizeForm, width: Math.ceil(meters / METERS_PER_CELL) });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg"
                      />
                      <span className="ml-2 text-gray-600 font-medium">m</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">2.5m ~ 25m</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      세로 (미터) *
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        step="0.5"
                        min="2.5"
                        max="25"
                        value={gridSizeRealForm.height_meters}
                        onChange={(e) => {
                          const meters = parseFloat(e.target.value) || 0;
                          setGridSizeRealForm({ ...gridSizeRealForm, height_meters: meters });
                          setGridSizeForm({ ...gridSizeForm, height: Math.ceil(meters / METERS_PER_CELL) });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg"
                      />
                      <span className="ml-2 text-gray-600 font-medium">m</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">2.5m ~ 25m</p>
                  </div>
                </div>

                {/* 면적 계산 및 참고 크기 */}
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-800 font-medium">
                    📐 새로운 크기: {gridSizeRealForm.width_meters}m × {gridSizeRealForm.height_meters}m
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    = <strong>{(gridSizeRealForm.width_meters * gridSizeRealForm.height_meters).toFixed(1)}㎡</strong>
                    <span className="mx-1">≈</span>
                    <strong>{((gridSizeRealForm.width_meters * gridSizeRealForm.height_meters) / 3.3).toFixed(1)}평</strong>
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    → 그리드: {gridSizeForm.width} × {gridSizeForm.height} 칸 (1칸 = {METERS_PER_CELL}m)
                  </p>
                </div>

                {/* 참고 크기 */}
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-xs font-medium text-gray-700 mb-2">📏 참고 크기:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <span>• 소형 (10평): 약 6m × 5.5m</span>
                    <span>• 중형 (20평): 약 8m × 8m</span>
                    <span>• 대형 (30평): 약 10m × 10m</span>
                    <span>• 특대 (50평): 약 13m × 13m</span>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowGridSizeModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleUpdateGridSize}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    ✅ 크기 변경
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 자동 배치 모달 */}
        {showAutoLayoutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">좌석 자동 배치</h3>
                <button
                  onClick={() => setShowAutoLayoutModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900">
                    현재 좌석 수: <strong>{seats.length}개</strong>
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    기존 좌석들의 위치를 그리드 형태로 자동 재배치합니다.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    배치 방향
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAutoLayoutDirection('horizontal')}
                      className={`flex-1 px-3 py-2 text-sm rounded-md border ${
                        autoLayoutDirection === 'horizontal'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      가로 (→↓)
                    </button>
                    <button
                      onClick={() => setAutoLayoutDirection('vertical')}
                      className={`flex-1 px-3 py-2 text-sm rounded-md border ${
                        autoLayoutDirection === 'vertical'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      세로 (↓→)
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    열 수 (가로 좌석 개수) *
                  </label>
                  <input
                    type="number"
                    value={autoLayoutColumns}
                    onChange={(e) => setAutoLayoutColumns(Math.max(1, Math.min(seats.length, parseInt(e.target.value) || 1)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="1"
                    max={seats.length}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    1~{seats.length} 사이의 숫자를 입력하세요
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>미리보기:</strong> {autoLayoutColumns}열 x {Math.ceil(seats.length / autoLayoutColumns)}행
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    {autoLayoutDirection === 'horizontal'
                      ? '좌석 번호가 가로로 채워집니다 (1→2→3... 다음줄)'
                      : '좌석 번호가 세로로 채워집니다 (1↓2↓3... 다음열)'}
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowAutoLayoutModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={autoLayoutSaving}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleAutoLayout}
                    disabled={autoLayoutSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {autoLayoutSaving ? '재배치 중...' : '재배치'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 룸 추가 모달 */}
        {showRoomModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">새 룸 추가</h3>

              <div className="space-y-4">
                {/* 룸 이름 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    룸 이름 *
                  </label>
                  <input
                    type="text"
                    value={roomForm.name}
                    onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                    placeholder="예: 1층, 2층, 스터디룸 A"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 설명 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    설명
                  </label>
                  <textarea
                    value={roomForm.description}
                    onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                    placeholder="룸에 대한 간단한 설명 (선택사항)"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 크기 입력 방식 선택 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-4 mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={useRealSize}
                        onChange={() => setUseRealSize(true)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">실제 크기 입력 (추천)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={!useRealSize}
                        onChange={() => setUseRealSize(false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">직접 셀 수 입력</span>
                    </label>
                  </div>

                  {useRealSize ? (
                    <>
                      {/* 실제 크기 입력 (미터) */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            가로 길이
                          </label>
                          <div className="flex items-center">
                            <input
                              type="number"
                              value={realSizeForm.width_meters}
                              onChange={(e) => {
                                const meters = parseFloat(e.target.value) || 5;
                                setRealSizeForm({ ...realSizeForm, width_meters: meters });
                                setRoomForm({ ...roomForm, grid_width: Math.ceil(meters / METERS_PER_CELL) });
                              }}
                              min={3}
                              max={30}
                              step={0.5}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600 text-sm">m</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            세로 길이
                          </label>
                          <div className="flex items-center">
                            <input
                              type="number"
                              value={realSizeForm.height_meters}
                              onChange={(e) => {
                                const meters = parseFloat(e.target.value) || 5;
                                setRealSizeForm({ ...realSizeForm, height_meters: meters });
                                setRoomForm({ ...roomForm, grid_height: Math.ceil(meters / METERS_PER_CELL) });
                              }}
                              min={3}
                              max={30}
                              step={0.5}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600 text-sm">m</span>
                          </div>
                        </div>
                      </div>

                      {/* 계산된 그리드 미리보기 */}
                      <div className="mt-4 p-3 bg-blue-50 rounded-md">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-blue-800">📐 계산된 그리드 크기:</span>
                          <span className="font-bold text-blue-900">
                            {roomForm.grid_width} × {roomForm.grid_height} 칸
                          </span>
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          (1칸 = {METERS_PER_CELL}m, 좌석 1개 크기 기준)
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          면적: {realSizeForm.width_meters * realSizeForm.height_meters}㎡ (약 {Math.round(realSizeForm.width_meters * realSizeForm.height_meters / 3.3)}평)
                        </div>
                      </div>

                      {/* 참고 사이즈 */}
                      <div className="mt-3 text-xs text-gray-500">
                        <p className="font-medium mb-1">💡 일반적인 독서실 크기:</p>
                        <ul className="space-y-0.5 ml-3">
                          <li>• 소형 (20석 이하): 6m × 5m (약 10평)</li>
                          <li>• 중형 (30~50석): 10m × 8m (약 25평)</li>
                          <li>• 대형 (50석 이상): 15m × 12m (약 55평)</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 기존 셀 개수 입력 */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            가로 (셀 개수)
                          </label>
                          <input
                            type="number"
                            value={roomForm.grid_width}
                            onChange={(e) =>
                              setRoomForm({ ...roomForm, grid_width: parseInt(e.target.value) || 20 })
                            }
                            min={10}
                            max={60}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            세로 (셀 개수)
                          </label>
                          <input
                            type="number"
                            value={roomForm.grid_height}
                            onChange={(e) =>
                              setRoomForm({ ...roomForm, grid_height: parseInt(e.target.value) || 15 })
                            }
                            min={10}
                            max={60}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        * 1셀 = 약 {METERS_PER_CELL}m (좌석 1개 크기)
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowRoomModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    if (!roomForm.name.trim()) {
                      alert('룸 이름을 입력해주세요.');
                      return;
                    }

                    try {
                      // layoutRepository를 사용하여 룸 생성
                      const newRoom = await layoutRepository.createRoom({
                        name: roomForm.name,
                        description: roomForm.description,
                        grid_width: roomForm.grid_width,
                        grid_height: roomForm.grid_height,
                      });

                      alert('룸이 추가되었습니다.');
                      setShowRoomModal(false);

                      // 룸 목록 다시 로드
                      const roomsData = await layoutRepository.getRooms();
                      // rooms 상태 업데이트는 useLayoutData 내부에서 처리되므로
                      // 전체 데이터를 다시 로드
                      await loadAllData();
                    } catch (error) {
                      console.error('룸 생성 실패:', error);
                      alert('룸 생성에 실패했습니다.');
                    }
                  }}
                  disabled={!roomForm.name.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  추가
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 룸 이름 수정 모달 */}
        {showRoomEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">룸 수정</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">룸 이름 *</label>
                  <input
                    type="text"
                    value={roomEditForm.name}
                    onChange={(e) => setRoomEditForm({ ...roomEditForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                  <textarea
                    value={roomEditForm.description}
                    onChange={(e) => setRoomEditForm({ ...roomEditForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowRoomEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    if (!roomEditForm.name.trim()) {
                      alert('룸 이름을 입력해주세요.');
                      return;
                    }
                    try {
                      await layoutRepository.updateRoom(selectedRoom!, {
                        name: roomEditForm.name,
                        description: roomEditForm.description,
                      });
                      alert('룸 정보가 수정되었습니다.');
                      setShowRoomEditModal(false);
                      await loadAllData();
                    } catch (error) {
                      console.error('룸 수정 실패:', error);
                      alert('룸 수정에 실패했습니다.');
                    }
                  }}
                  disabled={!roomEditForm.name.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 룸 삭제 확인 모달 */}
        {showRoomDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h3 className="text-lg font-semibold text-red-600 mb-2">룸 삭제</h3>
              <p className="text-gray-700 mb-1">
                <span className="font-bold">'{rooms.find(r => r.id === selectedRoom)?.name}'</span> 룸을 삭제하시겠습니까?
              </p>
              <p className="text-sm text-gray-500 mb-4">
                룸 안에 좌석이나 구역이 있으면 삭제할 수 없습니다.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowRoomDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    try {
                      await layoutRepository.deleteRoom(selectedRoom!);
                      alert('룸이 삭제되었습니다.');
                      setShowRoomDeleteConfirm(false);
                      setSelectedRoom('');
                      await loadAllData();
                    } catch (error: any) {
                      console.error('룸 삭제 실패:', error);
                      alert(error.message || '룸 삭제에 실패했습니다.');
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 룸 순서 변경 모달 */}
        {showRoomOrderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">룸 순서 변경</h3>
                <button
                  onClick={() => setShowRoomOrderModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-md"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {rooms.map((room, index) => (
                  <div
                    key={room.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      selectedRoom === room.id ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveRoom(index, 'up')}
                          disabled={index === 0}
                          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="위로 이동"
                        >
                          <ArrowUpIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveRoom(index, 'down')}
                          disabled={index === rooms.length - 1}
                          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="아래로 이동"
                        >
                          <ArrowDownIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{room.name}</div>
                        <div className="text-xs text-gray-500">{room.grid_width}x{room.grid_height} 칸</div>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400">{index + 1}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowRoomOrderModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default IntegratedLayoutManagement;
